import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

/**
 * Edit a client's own portal credentials (username / password / destination email).
 *
 * Partial update: fields left blank are not sent. The user can edit just one
 * field without knowing the current password.
 *
 * Submits PATCH /api/catalog/my-portals/{portal_id}/credentials.
 */
export default function EditCredentialsDialog({
  portal,
  open,
  onOpenChange,
  onSuccess,
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [destinationEmail, setDestinationEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordRevealed, setPasswordRevealed] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);

  useEffect(() => {
    if (open && portal) {
      setUsername(portal.username || "");
      setPassword("");
      setPasswordRevealed(false);
      setDestinationEmail(portal.destination_email || "");
    }
  }, [open, portal]);

  const handleToggleReveal = async () => {
    if (passwordRevealed) {
      // Hide: clear field and restore masked state
      setPassword("");
      setPasswordRevealed(false);
      return;
    }

    // Reveal: fetch the stored password
    setRevealLoading(true);
    try {
      const res = await api.get(`/catalog/my-portals/${portal.portal_id}/password`);
      setPassword(res.data?.password ?? "");
      setPasswordRevealed(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("No password on file for this portal");
      } else {
        toast.error("Couldn't reveal password. Try again.");
      }
    } finally {
      setRevealLoading(false);
    }
  };

  if (!portal) return null;

  const portalHost = (() => {
    if (!portal.url) return null;
    try {
      return new URL(portal.url).hostname.replace(/^www\./, "");
    } catch {
      return portal.url.replace(/^https?:\/\//, "").split("/")[0];
    }
  })();

  const handleSave = async () => {
    const payload = {};
    if (username && username !== portal.username) payload.username = username;
    if (password) payload.password = password;
    if (destinationEmail && destinationEmail !== portal.destination_email) {
      payload.destination_email = destinationEmail;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      await api.patch(
        `/catalog/my-portals/${portal.portal_id}/credentials`,
        payload
      );
      toast.success("Saved, we'll check it on the next run");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update credentials"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit credentials</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-medium text-gray-900">
              {portal.display_name || portal.canonical_name || portal.portal_name}
            </span>
            {portalHost ? (
              <a
                href={portal.url}
                target="_blank"
                rel="noreferrer"
                className="truncate underline"
                style={{ color: BRAND.text, maxWidth: "100%" }}
                title={portal.url}
              >
                {portalHost}
              </a>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cred-username">Username</Label>
            <Input
              id="cred-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cred-password">Password</Label>
            <div className="relative">
              <Input
                id="cred-password"
                type={passwordRevealed ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to keep current password"
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                aria-label={passwordRevealed ? "Hide password" : "Reveal password"}
                onClick={handleToggleReveal}
                disabled={revealLoading}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50"
              >
                {passwordRevealed ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Current password is hidden. Enter a new one here to change it.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cred-email">2FA destination email</Label>
            <Input
              id="cred-email"
              type="email"
              value={destinationEmail}
              onChange={(e) => setDestinationEmail(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-gray-500">
              Where this portal sends verification codes.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            className="text-white"
            style={{ backgroundColor: BRAND.bg }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = BRAND.hover)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = BRAND.bg)
            }
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
